<template>
  <div>
    <SidebarLayout
      :breadcrumb="breadcrumb"
      :rootPath="rootPath"
      :pageTitle="pageTitle"
      class="points-update-page"
    >
      <template #page-description>
        <p class="subtitle">
          Para visualizar os pontos de cada categoria,
          <router-link
            :to="`/hoteis/calendario-pontos/${hotelId}`"
            class="text-link subtitle-link"
          >
            acesse o calendário de pontos.
          </router-link>
        </p>
      </template>

      <template #sidebar>
        <!-- NOME DO HOTEL -->
        <InfoPanel height="103" class="info-panel">
          <template #content>
            <Heading class="info-panel--title heading--size-2">
              {{ hotel.nome }}
            </Heading>
          </template>
        </InfoPanel>
        <SidebarBlock class="page-actions">
          <!-- MENU DOS HOTÉIS -->
          <PmwHotelNav
            :links="menuLinks"
            :hide="isHotelAbsolute ? ['diferenciais', 'banners'] : ['banners']"
            active="pontos"
            class="menu-nav"
          />
          <Heading>Ações da página</Heading>

          <Button
            :disabled="disableApplyToCalendar"
            tooltipText="Selecione os dias da semana e os quartos para aplicar."
            @click="confirmRatesUpdate()"
          >
            Aplicar ao calendário
          </Button>

          <Button
            :disabled="disablePreviewButton"
            layout="outline"
            tooltipText="Selecione os dias da semana e os quartos para visualizar."
            @click="showPreviewModal = true"
          >
            Pré-visualizar
          </Button>
        </SidebarBlock>
      </template>

      <template #content>
        <div class="content">
          <UpdatePoints v-model="rates" :rooms="rooms" />
        </div>

        <!-- MODAL DE CONFIRMAÇÃO -->
        <ConfirmationModal
          v-model="showConfirmationModal"
          :formFields="JSON.parse(JSON.stringify(rates))"
          :rooms="rooms"
          :selectedGuestsOptions="selectedGuestsOptions"
          :hotel="hotel"
          @confirmModal="saveRatesUpdate()"
        />

        <!-- MODAL DE PRÉ-VISUALIZAÇÃO -->
        <PreviewCalendarDialog
          v-model="showPreviewModal"
          :roomOptions="selectedRoomsOptions"
          :guestOptions="selectedGuestsOptions"
          :datePeriods="previewCalendarPeriods"
          :roomsRatesRules="formatRoomRates.tarifas"
          class="preview-calendar-modal"
        />
        <!-- MODAL COM LOADER -->
        <LoaderModal v-model="showLoaderModal" title="Importante">
          <template #text>
            Aguarde enquanto atualizamos as informações. <br />
            Isso pode levar alguns minutos.
          </template>
        </LoaderModal>
      </template>
    </SidebarLayout>
  </div>
</template>

<script>
import { capitalizeString } from '@/helpers/string.js'
import SidebarLayout from '@/layouts/SidebarLayout'
import InfoPanel from '@solucoes/components-lib-tmp/src/components/patterns/InfoPanel'
import SidebarBlock from '@/components/_base/patterns/SidebarBlock'
import Heading from '@/components/_base/elements/Heading'
import Button from '@/components/_base/elements/Button'
import UpdatePoints from '@/components/hotel/points/UpdatePoints'
import ConfirmationModal from '@/components/hotel/points/ConfirmationModal'
import PreviewCalendarDialog from '@/components/hotel/points/PreviewCalendarDialog'
import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'
import hotelPointsMixin from '@/mixins/hotelPointsMixin'
import LoaderModal from '@/components/_base/commonComponents/LoaderModal'

export default {
  name: 'PointsUpdate',
  mixins: [hotelCommonsMixin, hotelPointsMixin],
  components: {
    SidebarLayout,
    InfoPanel,
    SidebarBlock,
    Heading,
    Button,
    UpdatePoints,
    PreviewCalendarDialog,
    ConfirmationModal,
    LoaderModal
  },
  data() {
    return {
      showPreviewModal: false /** Modal de pré-visualização do calendário */,
      selectedTab: null,
      rooms: [],
      rates: {
        dates: [],
        editedRates: [],
        rate: []
      },
      showLoaderModal: false /** Exibição da modal com loader */,
      isHotelAbsolute: false
    }
  },
  computed: {
    /**
     * Verifica se existe ao menos 1 quarto com 1 valor selecionado
     * para liberar a aplicação no calendário
     */
    disableApplyToCalendar() {
      let willDisable = false

      /** Se não tem tarifas e restrições, desabilita */
      if (this.rates.dates.length === 0) return true

      /** Se foram selecionados perídos para atualizar valores */
      if (this.rates.dates.length > 0) {
        /** Se não tem valores selecionados cancela */
        const hasValues = this.formatRoomRates.tarifas
          .flatMap((item) => item.quartos)
          .flatMap((rooms) => rooms.valores)
          .some(
            (item) =>
              item.quantidadePontos !== null && item.quantidadePontos >= 0
          )

        const hasDiscount = this.formatRoomRates.tarifas
          .flatMap((item) => item.quartos)
          .some((rooms) => rooms.desconto !== null && rooms.desconto >= 0)

        willDisable = hasValues || hasDiscount ? false : true
      }

      return willDisable
    },

    /** Verifica se existe ao menos 1 quarto com 1 valor selecionado para pré-visualizar */
    disablePreviewButton() {
      let willDisable = false

      /** Se não tem datas, desabilita */
      if (this.rates.dates.length === 0) return true

      /** Se não tem selecionados cancela */
      willDisable = !this.formatRoomRates.tarifas
        .flatMap((item) => item.quartos)
        .flatMap((rooms) => rooms.valores)
        .some((item) => item.valor !== null)

      return willDisable
    },

    /**
     * Retorna um array com as opções dos quartos selecionados
     * e que possue dias da semana e valores selecionados
     * para ser usada na modal de preview do calendário
     */
    selectedRoomsOptions() {
      return this.rooms.reduce((acc, item) => {
        /**
         * Analisa somente o primeiro item do array
         * porque todos períodos possuem os mesmos quartos
         */
        const match = this.formatRoomRates.tarifas[0]?.quartos.findIndex(
          (selectedItem) => selectedItem.codigo === item.codigo
        )

        if (match > -1) {
          const obj = {
            text: item.nome,
            value: item.codigo
          }

          acc.push(obj)
        }

        return acc
      }, [])
    },

    /**
     * Retorna as opções possíveis de hóspedes para os quartos selecionados
     * para ser usada na modal de preview do calendário
     */
    selectedGuestsOptions() {
      const guestsOptions = this.rates.editedRates.reduce((acc, curr) => {
        /** Pega as chaves do objeto e guarda as referentes a hóspedes */
        const paxKeys = Object.keys(curr.rules[0]).filter(
          (item) => item.search(/^(pax|criancas)[0-9]{1,}/gm) > -1
        )

        paxKeys.forEach((key) => {
          const foundAccKey = acc.findIndex((accKey) => accKey.key === key)

          if (foundAccKey < 0) {
            acc.push({
              rooms: [curr.code],
              key: key
            })
          } else {
            acc[foundAccKey].rooms.push(curr.code)
          }
        })
        return acc
      }, [])

      /** Ordena as opções */
      const pax = guestsOptions
        .filter((item) => item.key.search(/^pax/gim) > -1)
        .sort()
      const children = guestsOptions
        .filter((item) => item.key.search(/^criancas/gim) > -1)
        .sort()

      /** Monta um objeto com o formato aceito pelo componente */
      return [...pax, ...children].map((item) => {
        const obj = {}

        let text = item.key.replace('pax', 'Pax ')
        text = text.replace('criancas', 'Criança ')
        text = text.replace('crianca', 'Criança ')

        obj.text = text
        obj.value = item.key
        obj.rooms = item.rooms

        return obj
      })
    },

    /** Retorna as possíveis opções de períodos selecionados */
    previewCalendarPeriods() {
      return this.rates.dates
    },

    /** Retorna as tarifas selecionadas no formato esperado pelo backend */
    formatRoomRates() {
      const obj = {
        tarifas: []
      }

      /** Itera por cada período para criar o objeto */
      this.rates.dates.forEach((period) => {
        const finalObj = {
          dataInicial: period.start,
          dataFinal: period.end,
          quartos: []
        }

        /** Itera pelas regras de tarifas definidas */
        this.rates.editedRates.forEach((room) => {
          /** Itera pelos quartos para pegar os valores e montar o obj */
          room.rules.forEach((roomRule) => {
            const roomObj = {
              codigo: roomRule.roomCode,
              diasSemana: roomRule.selectedWeekDays.flatMap((day) => day.value),
              tipoAtualizacaoValor:
                roomRule.desconto !== null && roomRule.desconto >= 0 ? 2 : 1,
              desconto: roomRule.desconto,
              valores: this.buildRoomRulesObj(roomRule)
            }

            /** Somente adiciona os que possuem dias da semana e algum valor inserido */
            if (
              roomObj.diasSemana.length > 0 &&
              (roomObj.valores.length > 0 || roomObj.desconto > 0)
            ) {
              finalObj.quartos.push(roomObj)
            }
          })
        })

        if (finalObj.quartos.length > 0) {
          obj.tarifas.push(finalObj)
        }
      })
      return obj
    }
  },
  async created() {
    /** Chamada de dados dos Quartos do hotel */
    await this.$room.get(this.hotelId).then((response) => {
      this.rooms = response.data.filter(function(item) {
        return item.ativo
      })
      this.rooms.map((r) => {
        r.nome = capitalizeString(r.nome.toLowerCase())
      })
    })

    await this.$hotel.get(this.hotelId).then((response) => {
      this.isHotelAbsolute = response.data.tarifarioAbsoluto
    })
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}

.content {
  padding: 30px 50px 40px 50px;
  background-color: $white;
}

.info-panel {
  background: $pmw-gray-lightest;

  &--title {
    margin-bottom: 0px;
    color: $pmw-gray-darkest;
  }
}

.subtitle {
  font-size: font-size('size-medium');
  font-weight: font-weight('weight-light');
  margin: 20px 0 -4px 0;
  &-link {
    font-weight: font-weight('weight-normal');
  }
}

.sidebar-block {
  padding-top: 25px;
  .menu-nav {
    max-height: calc(100vh - 310px);
    overflow-y: scroll;
  }
}

.alert-chip-success {
  background: $pmw-success;

  &--link {
    text-decoration: underline;
    margin-left: 65px;
  }
}

.page-actions {
  padding-bottom: 10px;
  .heading {
    margin-bottom: 15px;
  }

  ::v-deep {
    .button-wrapper {
      & + .button-wrapper {
        margin-top: 10px;
      }
    }
  }
}

.points-update-page {
  ::v-deep {
    .content {
      &--white_card {
        background-color: $gray-lightest;
      }
    }

    .page-title {
      padding: 40px 50px 56px;
    }
  }
}
</style>
